import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Globe, GlobeLock, FileText, ExternalLink, Bot, Sparkles } from "lucide-react";
import { DEFAULT_SECTIONS } from "../components/LandingSections";
import ChatHint from "../components/ChatHint";
import EmptyStateCards from "../components/EmptyStateCards";

const STATUS_COLOR = {
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  unpublished: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
const STATUS_LABEL = { draft: "Entwurf", published: "Online", unpublished: "Offline" };

export default function Seiten() {
  const nav = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newForm, setNewForm] = useState({ title: "", slug: "" });
  const [confirmDel, setConfirmDel] = useState(null);
  const [publishDialog, setPublishDialog] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/landing-pages");
      setPages(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60);

  const createPage = async (e) => {
    e.preventDefault();
    const slug = newForm.slug || slugify(newForm.title) || "neue-seite";
    try {
      const { data } = await api.post("/landing-pages", {
        title: newForm.title, slug, sections: DEFAULT_SECTIONS(),
      });
      toast.success("Seite angelegt");
      setCreateOpen(false);
      setNewForm({ title: "", slug: "" });
      nav(`/seiten/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Konnte Seite nicht anlegen");
    }
  };

  const duplicate = async (p) => {
    try {
      await api.post(`/landing-pages/${p.id}/duplicate`);
      toast.success("Seite dupliziert");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/landing-pages/${confirmDel.id}`);
      toast.success("Seite gelöscht");
      setConfirmDel(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const publish = async () => {
    try {
      await api.post(`/landing-pages/${publishDialog.id}/publish`, { confirm: true });
      toast.success("Seite veröffentlicht");
      setPublishDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const unpublish = async (p) => {
    try {
      await api.post(`/landing-pages/${p.id}/unpublish`);
      toast.success("Seite offline");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const copyUrl = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Link kopiert"); }
    catch { toast("Link: " + url); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="seiten-page">
      <header className="flex items-start justify-between mb-6 gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-400">Marketing</div>
          <h1 className="text-3xl font-extrabold tracking-tight mt-1 text-white">Seiten</h1>
          <p className="text-sm text-slate-400 mt-2">{pages.length === 0 ? "Noch leer." : `${pages.length} Landingpages.`}</p>
          <div className="mt-2"><ChatHint text={'Sag dem KI-Agenten: „Baue eine Landingpage für mein Angebot."'} /></div>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="add-page-button" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold shadow-lg shadow-emerald-500/20">
          <Plus className="h-4 w-4 mr-2" /> Neue Seite
        </Button>
      </header>

      <Card className="bg-[#111827] border-white/5">
        <CardContent className="p-0">
          {loading ? <div className="p-10 text-center text-slate-400">Lade…</div>
            : pages.length === 0 ? (
              <EmptyStateCards
                testid="seiten-empty"
                title="Noch keine Seiten"
                subtitle="Eine Landingpage sammelt automatisch neue Leads — in 2 Minuten online."
                cards={[
                  { icon: Plus, title: "Seite anlegen", body: "Titel reicht — fertige Abschnitte lädst du im Editor.", onClick: () => setCreateOpen(true), testid: "empty-card-create" },
                  { icon: Bot, title: "KI-Agent bauen lassen", body: "Beschreibe das Angebot — der Agent legt die Seite an.", to: "/ki-agent", testid: "empty-card-chat" },
                  { icon: Sparkles, title: "Vorlage im Projekt", body: "Projekt-Vorlagen enthalten eine Landingpage-Vorlage.", to: "/alle-projekte", testid: "empty-card-template" },
                ]}
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-slate-400">Seite</TableHead>
                    <TableHead className="text-slate-400">Slug</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400 text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pages.map((p) => (
                    <TableRow key={p.id} className="border-white/5 hover:bg-white/[0.02]" data-testid={`page-row-${p.id}`}>
                      <TableCell>
                        <div className="font-medium text-white">{p.title}</div>
                        <div className="text-[11px] text-slate-500">{(p.sections || []).length} Section(s)</div>
                      </TableCell>
                      <TableCell className="text-slate-400 font-mono text-xs">/p/{p.slug}</TableCell>
                      <TableCell>
                        <Badge className={`${STATUS_COLOR[p.status]} border font-normal`} data-testid={`page-status-${p.id}`}>
                          {STATUS_LABEL[p.status] || p.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {p.status === "published" && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => copyUrl(p.public_url)}
                              data-testid={`page-copy-${p.id}`} className="text-emerald-300 hover:text-emerald-200">
                              <Copy className="h-4 w-4" />
                            </Button>
                            <a href={p.public_url} target="_blank" rel="noreferrer" data-testid={`page-open-${p.id}`}
                              className="inline-flex items-center justify-center h-8 w-8 text-emerald-300 hover:text-emerald-200">
                              <ExternalLink className="h-4 w-4" />
                            </a>
                            <Button variant="ghost" size="sm" onClick={() => unpublish(p)}
                              data-testid={`page-unpublish-${p.id}`} className="text-amber-300 hover:text-amber-200">
                              <GlobeLock className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {p.status !== "published" && (
                          <Button variant="ghost" size="sm" onClick={() => setPublishDialog(p)}
                            data-testid={`page-publish-${p.id}`} className="text-emerald-300 hover:text-emerald-200">
                            <Globe className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => nav(`/seiten/${p.id}`)}
                          data-testid={`page-edit-${p.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => duplicate(p)}
                          data-testid={`page-duplicate-${p.id}`} className="text-slate-300 hover:text-white hover:bg-white/5">
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmDel(p)}
                          data-testid={`page-delete-${p.id}`} className="text-red-400 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-[#111827] border-white/10 text-slate-100" data-testid="create-page-dialog">
          <DialogHeader><DialogTitle>Neue Seite</DialogTitle></DialogHeader>
          <form onSubmit={createPage} className="space-y-3">
            <div>
              <Label className="text-slate-300 text-xs mb-1 block">Titel</Label>
              <Input value={newForm.title} onChange={(e) => setNewForm({ ...newForm, title: e.target.value, slug: newForm.slug || slugify(e.target.value) })} required data-testid="create-page-title" placeholder="z. B. Sommer-Kampagne" />
            </div>
            <details className="group">
              <summary className="cursor-pointer text-xs font-medium text-slate-400 hover:text-slate-200 select-none inline-flex items-center gap-1" data-testid="create-page-advanced-toggle">
                <span className="group-open:rotate-90 transition inline-block">▸</span> Erweitert (URL-Pfad)
              </summary>
              <div className="mt-2">
                <Label className="text-slate-300 text-xs mb-1 block">Slug (URL-Pfad)</Label>
                <Input value={newForm.slug} onChange={(e) => setNewForm({ ...newForm, slug: slugify(e.target.value) })} placeholder="meine-landing-page" data-testid="create-page-slug" />
                <div className="text-[10px] text-slate-500 mt-1">Öffentlich unter /p/{newForm.slug || slugify(newForm.title) || "..."}</div>
              </div>
            </details>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)} data-testid="create-page-cancel">Abbrechen</Button>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold" data-testid="create-page-submit">Seite anlegen</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!publishDialog} onOpenChange={(o) => !o && setPublishDialog(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Seite veröffentlichen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              „{publishDialog?.title}" wird öffentlich unter <code className="text-slate-300">/p/{publishDialog?.slug}</code> erreichbar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="publish-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={publish} data-testid="publish-confirm" className="bg-emerald-500 hover:bg-emerald-600">Veröffentlichen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#111827] border-white/10 text-slate-100">
          <AlertDialogHeader>
            <AlertDialogTitle>Seite löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">„{confirmDel?.title}" wird dauerhaft entfernt.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="page-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="page-delete-confirm" className="bg-red-500 hover:bg-red-600">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
